import React, { useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    Select,
    MenuItem,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useAppContext } from '../../context/AppContext';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData
} from 'chart.js';
import SkinConditionLabels, { getConcernTranslation } from '../../services/concern-mappings';
import { UserSkinProfile } from '../../types';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);


type TimePeriod = '6months' | '1year';
type ConcernFilter = 'All' | string;

export const concernColors = [
    { border: '#4169E1', background: 'rgba(65,105,225,0.2)' },  // Royal Blue
    { border: '#DC143C', background: 'rgba(220,20,60,0.2)' },     // Crimson
    { border: '#228B22', background: 'rgba(34,139,34,0.2)' },     // Forest Green
    { border: '#DAA520', background: 'rgba(218,165,32,0.2)' },    // Goldenrod
    { border: '#9932CC', background: 'rgba(153,50,204,0.2)' },    // Dark Orchid
    { border: '#008080', background: 'rgba(0,128,128,0.2)' },     // Teal
    { border: '#6A5ACD', background: 'rgba(106,90,205,0.2)' },    // Slate Blue
    { border: '#FF6347', background: 'rgba(255,99,71,0.2)' },     // Tomato
    { border: '#191970', background: 'rgba(25,25,112,0.2)' },     // Midnight Blue
    { border: '#800000', background: 'rgba(128,0,0,0.2)' },       // Maroon
    { border: '#1E90FF', background: 'rgba(30,144,255,0.2)' },    // Dodger Blue
    { border: '#C71585', background: 'rgba(199,21,133,0.2)' },    // Medium Violet Red
    { border: '#FF69B4', background: 'rgba(255,105,180,0.2)' },   // Hot Pink
    { border: '#2E8B57', background: 'rgba(46,139,87,0.2)' },     // Sea Green
    { border: '#CD853F', background: 'rgba(205,133,63,0.2)' },    // Peru
    { border: '#708090', background: 'rgba(112,128,144,0.2)' }    // Slate Gray
];

interface MultiConcernChartProps {
    profileData: UserSkinProfile[];
    clickedUserId?: string;
}

const MultiConcernChart: React.FC<MultiConcernChartProps> = ({ profileData, clickedUserId }) => {
    const { getTranslation } = useAppContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.down('md'));

    const [timePeriod, setTimePeriod] = useState<TimePeriod>('6months');
    const [concernFilter, setConcernFilter] = useState<ConcernFilter>('All');

    /** Dropdown menu list (MuiMenu-list); zero left padding so global [dir="ltr"] ul rules do not indent it */
    const selectMenuProps = {
        MenuListProps: {
            sx: { paddingLeft: '0 !important' },
        },
    };

    const processedData = useMemo(() => {
        // Filter data based on clickedUserId if provided
        let filteredProfileData = profileData;
        if (clickedUserId) {
            // Find the clicked user's data and show only their sessions
            const clickedUser = profileData.find(profile => profile._id === clickedUserId);
            if (clickedUser) {
                // For anonymous users, show only their single session
                // For registered users, show all their sessions (filter by user_id if available)
                filteredProfileData = profileData.filter(profile => 
                    profile._id === clickedUserId || 
                    (profile.user_id && clickedUser.user_id && profile.user_id === clickedUser.user_id)
                );
            }
        }

        const sortedProfiles = [...filteredProfileData].sort((a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        const uniqueConcerns = Array.from(
            new Set(sortedProfiles.flatMap(profile =>
                profile.output_score.map(score => score.concern)
            ))
        ).filter(concern => concern !== 'skin_health');

        const currentDate = new Date();
        const filteredProfiles = sortedProfiles.filter(profile => {
            const profileDate = new Date(profile.createdAt);
            const monthsDiff = (currentDate.getFullYear() - profileDate.getFullYear()) * 12 +
                (currentDate.getMonth() - profileDate.getMonth());

            switch (timePeriod) {
                case '6months': return monthsDiff <= 6;
                case '1year': return monthsDiff <= 12;
            }
        });

        const datasets = uniqueConcerns.map((concern, index) => {
            const color = concernColors[index % concernColors.length];
            return {
                _id: concern,
                label: getTranslation(SkinConditionLabels[concern]),
                data: filteredProfiles.map(profile => {
                    const concernScore = profile.output_score.find(score => score.concern === concern);
                    return concernScore ? concernScore.score : 0;
                }),
                borderColor: color.border,
                backgroundColor: color.background,
            };
        });

        return {
            labels: filteredProfiles.map(profile =>
                new Date(profile.createdAt).toLocaleDateString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: '2-digit'
                })
            ),
            datasets: datasets,
            uniqueConcerns: uniqueConcerns
        };
    }, [profileData, timePeriod, clickedUserId, getTranslation]);

    const getFilteredDatasets = () => {
        if (concernFilter === 'All') {
            return processedData.datasets;
        } else {
            return processedData.datasets.filter(
                dataset => dataset._id === concernFilter
            );
        }
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                min: 0,
                max: 100,
                ticks: {
                    stepSize: isMobile ? 25 : 20,
                    font: {
                        size: isMobile ? 10 : 12
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)'
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    maxRotation: isMobile ? 45 : 0,
                    font: {
                        size: isMobile ? 10 : 12
                    },
                    autoSkip: true,
                    maxTicksLimit: isMobile ? 6 : (isMedium ? 10 : undefined)
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                padding: isMobile ? 8 : 10,
                displayColors: true,
                callbacks: {
                    label: function (context: any) {
                        return `${context.dataset.label}: ${context.parsed.y}`;
                    }
                }
            }
        },
        elements: {
            line: {
                tension: 0,
                borderWidth: 2,
                fill: false
            },
            point: {
                radius: 3,
                hoverRadius: isMobile ? 4 : 6,
                backgroundColor: 'white',
                borderWidth: 2
            }
        }
    };

    const data: ChartData<'line'> = {
        labels: processedData.labels,
        datasets: getFilteredDatasets()
    };

    return (
        <Card sx={{
            boxShadow: 3,
            borderRadius: 2,
            margin: '0 auto',
            width: "100%"
        }}>
            <CardContent sx={{
                padding: isMobile ? 1.5 : 2
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    mb: isMobile ? 1.5 : 2,
                    gap: isMobile ? 1 : 0
                }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" sx={{
                        fontWeight: 'medium',
                        fontSize: isMobile ? '14px' : '1.25rem',
                        mb: isMobile ? 1 : 0
                    }}>
                        {getTranslation("Txt321")}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: isMobile ? 'flex-end' : '',
                        gap: 1,
                        width: isMobile ? '100%' : 'auto',
                    }}>
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: isMobile ? '40%' : 120,
                                alignSelf: isMobile ? 'stretch' : 'auto'
                            }}
                        >
                            <Select
                                value={timePeriod}
                                onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
                                displayEmpty
                                MenuProps={selectMenuProps}
                                sx={{
                                    borderRadius: 1,
                                    fontSize: isMobile ? '0.875rem' : '1rem'
                                }}
                            >
                                <MenuItem value="6months">{getTranslation("Txt536")}</MenuItem>
                                <MenuItem value="1year">{getTranslation("Txt889")}</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: isMobile ? '40%' : 120,
                                alignSelf: isMobile ? 'stretch' : 'auto'
                            }}
                        >
                            <Select
                                value={concernFilter}
                                onChange={(e) => setConcernFilter(e.target.value as ConcernFilter)}
                                displayEmpty
                                MenuProps={selectMenuProps}
                                sx={{
                                    borderRadius: 1,
                                    fontSize: isMobile ? '0.875rem' : '1rem'
                                }}
                            >
                                <MenuItem value="All">{getTranslation("Txt890")}</MenuItem>
                                {processedData.uniqueConcerns.map((concern) => (
                                    <MenuItem key={concern} value={concern}>
                                {getConcernTranslation(getTranslation, concern)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box sx={{
                    height: isMobile ? 400 : (isMedium ? 500 : 600),
                    position: 'relative',
                    width: '100%'
                }}>
                    <Line options={options} data={data} />
                </Box>
                {concernFilter === 'All' && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 1.5,
                        mt: 2
                    }}>
                        {processedData.datasets.map((dataset, index) => (
                            <Box key={index} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                            }}>
                                <Box sx={{
                                    width: 12,
                                    height: 2,
                                    backgroundColor: dataset.borderColor,
                                    mr: 0.5
                                }} />
                                <Typography variant="body2" sx={{
                                    fontSize: 'inherit',
                                    color: 'text.secondary',
                                    textTransform: 'capitalize'
                                }}>
                                {getConcernTranslation(getTranslation, dataset.label)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default MultiConcernChart;